import React, { useState, useEffect, useRef } from 'react';
import {
  Popover,
  DialogContent,
  TextField,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import { CheckFilledIcon, CloseFilledIcon } from '../../../assets/icons/icons';

interface EditNotesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (notes: string) => void;
  currentNotes: string;
  anchorEl?: HTMLElement | null;
}

const EditNotesModal: React.FC<EditNotesModalProps> = ({
  open,
  onClose,
  onSave,
  currentNotes,
  anchorEl
}) => {
  const [notes, setNotes] = useState(currentNotes);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotes(currentNotes);
  }, [currentNotes]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Move cursor to end of text
          const length = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(length, length);
        }
      });
    }
  }, [open]);

  const handleSave = () => {
    onSave(notes);
    onClose();
  };

  const handleCancel = () => {
    setNotes(currentNotes);
    onClose();
  };

  return (
    <Popover
      open={open}
      onClose={handleCancel}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      sx={{
        '& .MuiPopover-paper': {
          transform: 'translateX(-30px) !important'
        }
      }}
      slotProps={{
        paper: {
          style: {
            borderRadius: 4,
            width: '220px',
            maxHeight: '200px',
            boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14)',
            padding: '8px 0'
          }
        }
      }}>
      <DialogContent sx={{ 
        p: '4px 16px', 
        maxHeight: '128px', 
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch'
      }}>
        <TextField
          inputRef={textareaRef}
          multiline
          rows={3}
          fullWidth
          variant="standard"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={`Enter notes about this official...\n(1,000 character limit)`}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              border: 'none',
              padding: 0,
              '&:before': { border: 'none' },
              '&:after': { border: 'none' },
              '&:hover:before': { border: 'none' }
            },
            '& .MuiInputBase-input': {
              minHeight: '80px',
              maxHeight: '128px',
              overflow: 'auto',
              color: '#2B455E',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '1.7',
              letterSpacing: '0.17px',
              padding: 0
            }
          }}
        />
      </DialogContent>
      <Divider sx={{ 
        mx: 0, 
        p: '8px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch'
      }} />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        alignSelf: 'stretch'
      }}>
        <Box sx={{ 
          display: 'flex', 
          padding: '4px 16px',
          alignItems: 'center',
          gap: 1
        }}>
          <IconButton onClick={handleSave} size="small" sx={{ 
            color: 'rgba(0, 0, 0, 0.56)',
            width: '24px',
            height: '24px'
          }}>
            <CheckFilledIcon />
          </IconButton>
          <IconButton onClick={handleCancel} size="small" sx={{ 
            color: 'rgba(0, 0, 0, 0.56)',
            width: '18px',
            height: '20px'
          }}>
            <CloseFilledIcon />
          </IconButton>
        </Box>
      </Box>
    </Popover>
  );
};

export default EditNotesModal;
